// Outreach — thread list. localStorage-only until Gmail OAuth lands.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatTile from '../components/domain/StatTile';
import Chip from '../components/ui/Chip';
import type { OutreachThread, OutreachStatus } from '../lib/types';

const STORAGE_KEY = 'nice2job.outreach';

function loadThreads(): OutreachThread[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as OutreachThread[]) : [];
  } catch {
    return [];
  }
}

function statusVariant(s: OutreachStatus) {
  if (s === 'Replied') return 'good';
  if (s === 'Sent') return 'primary';
  if (s === 'Bounced') return 'warn';
  return 'third';
}

export default function Outreach() {
  const nav = useNavigate();
  const [threads, setThreads] = useState<OutreachThread[]>([]);

  useEffect(() => setThreads(loadThreads()), []);

  const drafts = threads.filter((t) => t.status === 'Draft').length;
  const sent = threads.filter((t) => t.status === 'Sent').length;
  const replied = threads.filter((t) => t.status === 'Replied').length;

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="Workspace · warm intros"
        title="Your people, warmed up."
        sub="Drafts for hiring managers, recruiters, warm intros. Stored locally — draft here, send manually."
        actions={
          <>
            <button className="btn" onClick={() => nav('/templates')}>Templates</button>
            <button className="btn primary" onClick={() => nav('/outreach/compose')}>Compose</button>
          </>
        }
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
        <StatTile label="Drafts" value={drafts} sub="Ready to review" />
        <StatTile label="Sent" value={sent} />
        <StatTile label="Replied" value={replied} />
        <StatTile label="Reply rate" value={sent ? `${Math.round((replied / sent) * 100)}%` : '—'} />
      </div>
      {threads.length === 0 ? (
        <div className="card empty">
          <span className="serif">No outreach yet.</span>
          Draft one — we'll write three openers and you keep the one that sounds like you.
        </div>
      ) : (
        <div className="card">
          <div className="row-head" style={{ gridTemplateColumns: '1.2fr 1.4fr 2fr 0.8fr' }}>
            {['To', 'Role · Company', 'Subject', 'Status'].map((h) => <div key={h} className="tab">{h}</div>)}
          </div>
          {threads.map((t) => (
            <div
              key={t.id}
              className="row"
              style={{ gridTemplateColumns: '1.2fr 1.4fr 2fr 0.8fr' }}
              onClick={() => nav(`/outreach/${t.id}`)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                  {t.to.split(' ').map((s) => s[0]).join('')}
                </div>
                <span style={{ fontWeight: 500 }}>{t.to}</span>
              </div>
              <div style={{ color: 'var(--ink2)', fontSize: 12.5 }}>{t.role}</div>
              <div style={{ fontSize: 12.5 }}>{t.subject}</div>
              <div><Chip variant={statusVariant(t.status)}>{t.status}</Chip></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
